import { MonthClose } from "@/api/month_close.tsx"
import { OrgConfigState } from "@/org_config/state.tsx"
import { format, isValid, parseISO, startOfMonth } from "date-fns"

/** The fields the clearing-list rule reads off a deduction or transaction row. */
export interface ClearingListRow {
  posting_date?: string | null
  validated_date?: string | null
  status_value?: string
}

/**
 * A row counts as cleared while its *current* stage is settled: validated, or won (disputed and
 * repaid). `validated_date` alone is not enough — it records the first validation and survives a
 * later move back to Review or Disputed, which is how Saffron Road's August 2026 file showed rows
 * that were both "Disputed" and "Cleared".
 */
export function is_cleared_status(status_value: string | undefined): boolean {
  return status_value === "validated" || status_value === "won"
}

function parse_date(value: string | null | undefined): Date | null {
  if (!value) return null
  const parsed = parseISO(value)
  return isValid(parsed) ? parsed : null
}

/**
 * The month whose month-end close list a row went out on, as the first of that month, or null while
 * the row is still open.
 *
 * A month's books close a few days after it ends, and everything validated up to that day belongs
 * to that month: with June closed on 7/9, a deduction validated on 7/2 was cleared in June. So the
 * rule is: take the calendar month of the validation date, and if the previous month's recorded
 * close is on or after the validation date, step back to that month. Without a recorded close the
 * calendar month stands. A row can never clear before the month it was posted in, so the result is
 * floored at the posting month — a KeHE check dated 7/24 that the brand posted on 8/03 is an
 * August deduction even though we validated it on 7/30.
 *
 * This was the "Clearing List" column until Saffron Road's 9/25 call: their team (and PromoMash)
 * read Clearing List as the month the deduction *arrived*, so this rule was renamed Month-End Close
 * List and Clearing List became the posting month (`clearing_list_month`).
 */
export function month_end_close_list_month(row: ClearingListRow, closes: MonthClose[]): Date | null {
  if (!is_cleared_status(row.status_value)) return null
  const validated = parse_date(row.validated_date)
  if (!validated) return null

  let month = startOfMonth(validated)
  const earlier_closes = closes
    .map(close => ({ month: parse_date(close.month), closed_on: parse_date(close.closed_on) }))
    .filter((close): close is { month: Date; closed_on: Date } => close.month !== null && close.closed_on !== null)
    .filter(close => startOfMonth(close.month) < month)
    .sort((a, b) => b.month.getTime() - a.month.getTime())
  const previous = earlier_closes[0]
  if (previous && validated <= previous.closed_on) {
    month = startOfMonth(previous.month)
  }

  const posted = parse_date(row.posting_date)
  if (posted && startOfMonth(posted) > month) {
    month = startOfMonth(posted)
  }
  return month
}

/**
 * The month whose clearing list a row is on, in PromoMash's sense: the month the deduction hit the
 * books, i.e. its posting month, whether or not it has cleared yet. Null without a posting date.
 */
export function clearing_list_month(row: ClearingListRow): Date | null {
  const posted = parse_date(row.posting_date)
  return posted ? startOfMonth(posted) : null
}

function format_month(month: Date | null): string {
  return month ? format(month, "MMMM yyyy") : ""
}

/** `month_end_close_list_month` rendered the way the column shows it ("June 2026"), blank while open. */
export function format_month_end_close_list(row: ClearingListRow, closes: MonthClose[]): string {
  return format_month(month_end_close_list_month(row, closes))
}

/** `clearing_list_month` rendered the way the column shows it ("June 2026"), blank without a posting date. */
export function format_clearing_list(row: ClearingListRow): string {
  return format_month(clearing_list_month(row))
}

/**
 * The value of a computed month-list org field (`clearing_list`, `month_end_close_list`) for a row,
 * against the org's recorded closes; null for any other field.
 */
export function month_list_field_value(field_name: string, row: ClearingListRow): string | null {
  if (field_name === "clearing_list") return format_clearing_list(row)
  if (field_name === "month_end_close_list") {
    return format_month_end_close_list(row, OrgConfigState.get().monthCloses)
  }
  return null
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function close(month: string, closed_on: string): MonthClose {
    return { id: month, org_id: "org", month, closed_on, created_at: "", updated_at: "" }
  }
  // Saffron Road's 2026 closes.
  const CLOSES = [close("2026-08-01", "2026-09-02"), close("2026-06-01", "2026-07-09"), close("2026-07-01", "2026-08-05")]

  describe("month_end_close_list_month", () => {
    it("is blank while the row is open, whatever validated_date says", () => {
      expect(format_month_end_close_list({ status_value: "new", posting_date: "2026-06-09" }, CLOSES)).toBe("")
      // Validated once, then disputed: the first-validation date survives but the row is open again.
      expect(
        format_month_end_close_list({ status_value: "disputed", validated_date: "2026-07-06", posting_date: "2026-06-09" }, CLOSES)
      ).toBe("")
      expect(format_month_end_close_list({ status_value: "review", validated_date: "2026-08-10" }, CLOSES)).toBe("")
      expect(format_month_end_close_list({ status_value: "validated", validated_date: null }, CLOSES)).toBe("")
    })

    it("uses the calendar month of the validation date when nothing bleeds over", () => {
      expect(
        format_month_end_close_list({ status_value: "validated", validated_date: "2026-08-20", posting_date: "2026-06-17" }, CLOSES)
      ).toBe("August 2026")
      expect(format_month_end_close_list({ status_value: "validated", validated_date: "2026-07-15" }, CLOSES)).toBe("July 2026")
    })

    it("steps back to the previous month while that month's close was still open", () => {
      // June closed 7/9: validated 7/2 and 7/9 are June, 7/10 is July.
      expect(format_month_end_close_list({ status_value: "validated", validated_date: "2026-07-02" }, CLOSES)).toBe("June 2026")
      expect(format_month_end_close_list({ status_value: "validated", validated_date: "2026-07-09" }, CLOSES)).toBe("June 2026")
      expect(format_month_end_close_list({ status_value: "validated", validated_date: "2026-07-10" }, CLOSES)).toBe("July 2026")
      // August closed 9/2: the $249k row re-validated on 9/2 is on the August list.
      expect(
        format_month_end_close_list({ status_value: "validated", validated_date: "2026-09-02", posting_date: "2026-06-17" }, CLOSES)
      ).toBe("August 2026")
      expect(format_month_end_close_list({ status_value: "validated", validated_date: "2026-09-03" }, CLOSES)).toBe("September 2026")
    })

    it("never clears a row before the month it was posted in", () => {
      // KeHE check dated 7/24, validated 7/30, posted by the brand on 8/03: an August deduction.
      expect(
        format_month_end_close_list({ status_value: "validated", validated_date: "2026-07-30", posting_date: "2026-08-03" }, CLOSES)
      ).toBe("August 2026")
    })

    it("falls back to the calendar month when no close is recorded", () => {
      expect(format_month_end_close_list({ status_value: "validated", validated_date: "2026-07-02" }, [])).toBe("July 2026")
      // A close for a later month says nothing about earlier ones.
      expect(
        format_month_end_close_list({ status_value: "validated", validated_date: "2026-03-05" }, [close("2026-06-01", "2026-07-09")])
      ).toBe("March 2026")
    })

    it("treats a won row as cleared", () => {
      expect(format_month_end_close_list({ status_value: "won", validated_date: "2026-07-05" }, CLOSES)).toBe("June 2026")
    })

    it("ignores unparsable dates", () => {
      expect(format_month_end_close_list({ status_value: "validated", validated_date: "not-a-date" }, CLOSES)).toBe("")
      expect(
        format_month_end_close_list({ status_value: "validated", validated_date: "2026-07-02" }, [close("bad", "2026-07-09")])
      ).toBe("July 2026")
    })
  })

  describe("clearing_list_month", () => {
    it("is the posting month, open or cleared", () => {
      expect(format_clearing_list({ status_value: "new", posting_date: "2026-08-14" })).toBe("August 2026")
      // Validated on 9/2 inside August's close window, posted in June: on June's clearing list.
      expect(
        format_clearing_list({ status_value: "validated", validated_date: "2026-09-02", posting_date: "2026-06-17" })
      ).toBe("June 2026")
    })

    it("is blank without a usable posting date", () => {
      expect(format_clearing_list({ status_value: "validated", validated_date: "2026-07-02" })).toBe("")
      expect(format_clearing_list({ status_value: "new", posting_date: "not-a-date" })).toBe("")
    })
  })

  describe("month_list_field_value", () => {
    it("dispatches the two month-list fields and ignores the rest", () => {
      const row = { status_value: "validated", validated_date: "2026-09-10", posting_date: "2026-07-20" }
      expect(month_list_field_value("clearing_list", row)).toBe("July 2026")
      expect(month_list_field_value("month_end_close_list", row)).toBe("September 2026")
      expect(month_list_field_value("posting_date", row)).toBeNull()
    })
  })
}
